import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  pending_payment: "Ödeme Bekliyor",
  queued: "Sırada",
  in_progress: "Mixleniyor",
  awaiting_review: "Onay Bekliyor",
  revision_requested: "Revizyon Talep Edildi",
  completed: "Tamamlandı",
};

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revNotes, setRevNotes] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const askRevision = async (order) => {
    const notes = (revNotes[order.order_id] || "").trim();
    if (!notes) { toast.error("Revizyon notu gerekli"); return; }
    try {
      const r = await api.post(`/orders/${order.order_id}/revisions`, { notes });
      if (r.data.kind === "free") {
        toast.success("Revizyon talebiniz alındı");
        load();
      } else if (r.data.kind === "extra_required") {
        toast.warning("2 ücretsiz revizyon kullanıldı. Ekstra revizyon için ödeme gerekiyor.");
        // Trigger extra revision checkout
        const pay = await api.post("/payments/checkout", { order_id: order.order_id, kind: "extra_revision" });
        window.location.href = pay.data.checkout_url;
      }
    } catch (e) {
      toast.error("Talep gönderilemedi");
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono-caps mb-3">Müşteri Paneli</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Siparişlerim</h1>
        </div>
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" /> Yenile
        </button>
      </div>

      {loading && <div className="text-slate-500 mt-10">Yükleniyor…</div>}
      {!loading && orders.length === 0 && (
        <div className="panel p-10 mt-10 text-center">
          <div className="mono-caps text-slate-400">Sipariş yok</div>
          <div className="font-display text-xl mt-2 text-white">İlk siparişini oluşturmak için Sipariş Ver sayfasına git.</div>
        </div>
      )}

      <div className="space-y-6 mt-10">
        {orders.map((o) => (
          <div key={o.order_id} className="panel p-6" data-testid={`order-card-${o.order_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="mono-caps">{o.order_id}</div>
                <div className="font-display text-xl text-white mt-1">{o.track_name}</div>
                <div className="text-sm text-slate-400 mt-1">{o.package_name}{o.with_master ? " + Mastering" : ""} · {o.channels} kanal</div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={o.status} />
                <div className="font-display text-2xl text-white">{o.total_amount.toLocaleString("tr-TR")} ₺</div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="text-sm text-slate-300 space-y-1">
                <div className="mono-caps">Yüklenen Dosyalar</div>
                {o.raw_files.length === 0 && <div className="text-slate-500">Henüz dosya yok</div>}
                <ul className="space-y-1">
                  {o.raw_files.map((f) => (
                    <li key={f.file_id} className="truncate">• {f.original_filename}</li>
                  ))}
                </ul>
              </div>
              <div className="text-sm text-slate-300 space-y-1">
                <div className="mono-caps">Teslim Dosyaları</div>
                {o.deliverable_files.length === 0 && <div className="text-slate-500">Henüz teslim yok</div>}
                <ul className="space-y-2">
                  {o.deliverable_files.map((f) => (
                    <li key={f.file_id} className="flex items-center gap-2">
                      <a
                        href={fileUrl(f.storage_path)}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200"
                        data-testid={`download-${f.file_id}`}
                      >
                        <Download className="w-4 h-4" /> {f.label || f.original_filename}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-sm text-slate-300 space-y-1">
                <div className="mono-caps">Revizyon Sayaç</div>
                <div>Kullanılan: <span className="text-white">{o.revisions_used}</span> / Ücretsiz: <span className="text-white">{o.revisions_free}</span></div>
                <div>Ekstra ödenen: <span className="text-white">{o.extra_revisions_paid}</span></div>
              </div>
            </div>

            {o.payment_status === "paid" && o.deliverable_files.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="mono-caps mb-2">Revizyon Talep Et</div>
                <textarea
                  rows={3}
                  placeholder="Notlarını yaz..."
                  value={revNotes[o.order_id] || ""}
                  onChange={(e) => setRevNotes({ ...revNotes, [o.order_id]: e.target.value })}
                  data-testid={`revision-notes-${o.order_id}`}
                  className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none"
                />
                <button onClick={() => askRevision(o)} className="btn-ghost mt-3" data-testid={`request-revision-${o.order_id}`}>
                  {o.revisions_used < o.revisions_free ? "Revizyon Talep Et (Ücretsiz)" : "Ekstra Revizyon · 2.000 ₺ Öde"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </main>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_payment: "bg-amber-400/10 text-amber-300 border-amber-400/30",
    queued: "bg-cyan-400/10 text-cyan-300 border-cyan-400/30",
    in_progress: "bg-cyan-400/20 text-cyan-200 border-cyan-400/40",
    awaiting_review: "bg-amber-400/20 text-amber-200 border-amber-400/40",
    revision_requested: "bg-rose-500/10 text-rose-300 border-rose-500/30",
    completed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  };
  return (
    <span className={`mono-caps px-3 py-1 rounded-full border ${map[status] || ""}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
